import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { GlassPanel } from "./GlassPanel";

/**
 * §v3 — hero KPI card. Three slots: overline label, big number, and a
 * footer (delta, sub-stat, sparkline, whatever the page wants).
 *
 * Sizes match the kit's three KPI tiers:
 *   sm — 28px numeral. Used for secondary KPIs in tight rows.
 *   md — 44px numeral. Default. Used for primary KPI strips.
 *   xl — 56px numeral. Hero KPI on Analytics; one per page max.
 *
 * The number always renders with `font-num` (JetBrains Mono) and tabular
 * nums so 8s and 0s align vertically across cards. NEVER use the
 * `font-display` / Inter Tight here — we tried it and the proportional
 * digits made the strip look uneven.
 */

export type KpiSize = "sm" | "md" | "xl";

export interface KpiCardProps {
  /** Uppercase overline above the number ("REVENUE · THIS WEEK · AED"). */
  label: string;
  /** Big number content. Pre-format outside this component (commas,
   * decimals, currency); the component just renders what it's given. */
  value: ReactNode;
  /** Optional second line under the number (delta, sparkline target). */
  footer?: ReactNode;
  /** Optional decorative trail under the footer (sparkline, mini bar). */
  trail?: ReactNode;
  size?: KpiSize;
  className?: string;
}

const SIZE: Record<KpiSize, string> = {
  sm: "xpand-kpi xpand-kpi-sm",
  md: "xpand-kpi xpand-kpi-md",
  xl: "xpand-kpi xpand-kpi-xl",
};

export function KpiCard({
  label,
  value,
  footer,
  trail,
  size = "md",
  className,
}: KpiCardProps) {
  return (
    <GlassPanel
      variant="panel"
      rounded="g-lg"
      className={cn(
        "relative flex flex-col gap-2.5 overflow-hidden",
        // Hero KPI gets a faint warm glow at the bottom-left so the
        // amber canvas tint shows up only where it counts.
        size === "xl" &&
          "before:pointer-events-none before:absolute before:-bottom-12 before:-left-12 before:h-44 before:w-44 before:rounded-full before:opacity-50 before:blur-3xl before:[background:var(--canvas-tint-warm)]",
        // Comfortable density — matches the kit (18-20px padding,
        // grows on `xl`).
        size === "xl" ? "p-6" : "px-5 py-4",
        className,
      )}
    >
      <div className="xpand-overline">{label}</div>
      <div className={SIZE[size]}>{value}</div>
      {footer ? (
        <div className="font-num text-g-micro text-fg-3 leading-tight">
          {footer}
        </div>
      ) : null}
      {trail ? <div className="mt-1.5">{trail}</div> : null}
    </GlassPanel>
  );
}
